import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Bot,
  User as UserIcon,
  ExternalLink,
  FlaskConical,
  Loader2,
  Mail,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/StatusBadge';
import { callsApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import {
  CALL_STATUS_LABEL,
  CALL_STATUS_VARIANT,
  OUTCOME_LABEL,
  fmtDateTime,
  fmtDuration,
  fmtPhone,
  fmtUsd,
  EMAIL_REASON_LABEL,
  EMAIL_STATUS_LABEL,
  EMAIL_STATUS_VARIANT,
} from '@/lib/format';
import { MockWebhookSimulator } from './MockWebhookSimulator';
import { WebCallPanel } from './WebCallPanel';
import { useCan } from '@/features/auth/store';

/** Status que o Twilio reporta no callback de entrega. */
const SMS_STATUS_LABEL: Record<string, string> = {
  queued: 'Na fila',
  accepted: 'Aceito',
  sending: 'Enviando',
  sent: 'Enviado',
  delivered: 'Entregue',
  undelivered: 'Não entregue',
  failed: 'Falhou',
};

const SMS_STATUS_VARIANT: Record<string, string> = {
  delivered: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  sent: 'bg-sky-500/15 text-sky-600 dark:text-sky-400',
  failed: 'bg-destructive/15 text-destructive',
  undelivered: 'bg-destructive/15 text-destructive',
};

interface TranscriptTurn {
  role: 'agent' | 'user' | string;
  content: string;
}

export function CallDetailPage() {
  const { id = '' } = useParams();
  const canManage = useCan('calls.manage');
  // Token entregue pelo diálogo "Ligar no navegador"; some ao recarregar a página (ele expira em ~30 s).
  const webCallToken =
    (useLocation().state as { accessToken?: string } | null)?.accessToken ?? null;
  const q = useQuery({
    queryKey: ['calls', id],
    queryFn: () => callsApi.get(id),
    // Viva: atualiza a cada 5 s. Encerrada sem análise: continua por alguns minutos — o
    // call_analyzed chega depois do call_ended, e é ele que traz resultado e resumo.
    refetchInterval: (query) => {
      const c = query.state.data;
      if (!c) return false;
      if (['queued', 'ringing', 'in_progress'].includes(c.status)) return 5_000;
      const endedRecently = c.endedAt && Date.now() - new Date(c.endedAt).getTime() < 3 * 60_000;
      return c.status === 'ended' && !c.analysis && endedRecently ? 5_000 : false;
    },
  });
  const qc = useQueryClient();
  const sync = useMutation({
    mutationFn: () => callsApi.sync(id),
    onSuccess: (updated) => {
      qc.setQueryData(['calls', id], updated);
      void qc.invalidateQueries({ queryKey: ['leads'] });
      toast.success('Chamada sincronizada com o provedor');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Falha ao sincronizar'),
  });

  const sendEmail = useMutation({
    mutationFn: () => callsApi.email(id),
    onSuccess: (updated) => {
      qc.setQueryData(['calls', id], updated);
      toast.success('E-mail com o link enviado');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Falha ao enviar o e-mail'),
  });

  const call = q.data;
  if (q.isLoading) return <Skeleton className="h-64 w-full" />;
  if (!call) return <p className="text-muted-foreground">Chamada não encontrada.</p>;

  const turns = Array.isArray(call.transcriptJson)
    ? (call.transcriptJson as TranscriptTurn[]).filter((t) => t.content)
    : null;
  const live = ['queued', 'ringing', 'in_progress'].includes(call.status);
  const SENTIMENT: Record<string, string> = {
    Positive: 'Positivo',
    Negative: 'Negativo',
    Neutral: 'Neutro',
    Unknown: 'Desconhecido',
  };

  return (
    <div>
      <PageHeader
        title={`Chamada para ${call.lead?.name || fmtPhone(call.toNumber)}`}
        description={`${fmtDateTime(call.createdAt)} · ${fmtPhone(call.toNumber)}`}
        actions={
          <>
            {call.provider !== 'mock' && call.providerCallId && canManage && (
              <Button
                variant="outline"
                onClick={() => sync.mutate()}
                disabled={sync.isPending}
                title="Relê a chamada no provedor. Útil quando o webhook se perdeu."
              >
                {sync.isPending ? <Loader2 className="animate-spin" /> : <RefreshCw />} Sincronizar
              </Button>
            )}
            <Button variant="ghost" asChild>
              <Link to="/chamadas">
                <ArrowLeft /> Voltar
              </Link>
            </Button>
          </>
        }
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Resumo</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
                <dt className="text-muted-foreground">Status</dt>
                <dd>
                  <StatusBadge
                    label={CALL_STATUS_LABEL[call.status]}
                    className={CALL_STATUS_VARIANT[call.status]}
                  />
                </dd>
                <dt className="text-muted-foreground">Resultado</dt>
                <dd className="font-medium">
                  {call.outcome ? OUTCOME_LABEL[call.outcome] : live ? 'Aguardando…' : '—'}
                </dd>
                <dt className="text-muted-foreground">Sentimento</dt>
                <dd>{call.sentiment ? (SENTIMENT[call.sentiment] ?? call.sentiment) : '—'}</dd>
                <dt className="text-muted-foreground">Duração</dt>
                <dd>{fmtDuration(call.durationSec)}</dd>
                <dt className="text-muted-foreground">Custo</dt>
                <dd>{fmtUsd(call.costUsd)}</dd>
                <dt className="text-muted-foreground">Início</dt>
                <dd>{fmtDateTime(call.startedAt)}</dd>
                <dt className="text-muted-foreground">Fim</dt>
                <dd>{fmtDateTime(call.endedAt)}</dd>
                <dt className="text-muted-foreground">Idioma</dt>
                <dd>{call.language}</dd>
                <dt className="text-muted-foreground">Provedor</dt>
                <dd>{call.provider}</dd>
                <dt className="text-muted-foreground">Canal</dt>
                <dd>{call.direction === 'web' ? 'Navegador' : 'Telefone'}</dd>
                <dt className="text-muted-foreground">De</dt>
                <dd className="tabular-nums">{call.fromNumber ?? '—'}</dd>
                <dt className="text-muted-foreground">Encerramento</dt>
                <dd className="font-mono text-xs">{call.disconnectReason ?? '—'}</dd>
                <dt className="text-muted-foreground">Lead</dt>
                <dd>
                  <Link className="underline" to={`/leads/${call.leadId}`}>
                    ver lead
                  </Link>
                </dd>
                {call.campaignId && (
                  <>
                    <dt className="text-muted-foreground">Campanha</dt>
                    <dd>
                      <Link className="underline" to={`/campanhas/${call.campaignId}`}>
                        ver campanha
                      </Link>
                    </dd>
                  </>
                )}
                <dt className="text-muted-foreground">ID provedor</dt>
                <dd className="break-all font-mono text-[11px]">{call.providerCallId ?? '—'}</dd>
              </dl>
              {call.errorMessage && (
                <p className="mt-3 rounded bg-destructive/10 p-2 text-xs text-destructive">
                  {call.errorMessage}
                </p>
              )}
            </CardContent>
          </Card>

          {call.analysis && Object.keys(call.analysis).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Análise pós-chamada</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
                  {Object.entries(call.analysis)
                    // já exibidos em "Resumo da IA" e no card de resumo
                    .filter(
                      ([k]) =>
                        ![
                          'summary',
                          'call_summary',
                          'user_sentiment',
                          'custom_analysis_data',
                        ].includes(k),
                    )
                    .map(([k, v]) => (
                      <span key={k} className="contents">
                        <dt className="text-muted-foreground">{k}</dt>
                        <dd className="[overflow-wrap:anywhere]">
                          {typeof v === 'boolean' ? (v ? 'Sim' : 'Não') : String(v ?? '—')}
                        </dd>
                      </span>
                    ))}
                </dl>
              </CardContent>
            </Card>
          )}

          {call.dynamicVars && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Variáveis enviadas</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 text-xs">
                  {Object.entries(call.dynamicVars).map(([k, v]) => (
                    <span key={k} className="contents">
                      <dt className="font-mono text-muted-foreground">{k}</dt>
                      <dd className="[overflow-wrap:anywhere]">
                        {String(v) || <span className="text-muted-foreground">vazio</span>}
                      </dd>
                    </span>
                  ))}
                </dl>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-4 lg:col-span-2">
          {webCallToken && (
            <WebCallPanel accessToken={webCallToken} onEnded={() => void q.refetch()} />
          )}
          {call.summary && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Resumo da IA</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-relaxed">{call.summary}</p>
              </CardContent>
            </Card>
          )}
          {call.smsMessages?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">SMS enviados</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {call.smsMessages.map((m) => (
                  <div key={m.id} className="rounded-lg border p-3 text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-mono text-xs text-muted-foreground">{m.toE164}</span>
                      <StatusBadge
                        label={SMS_STATUS_LABEL[m.status] ?? m.status}
                        className={
                          SMS_STATUS_VARIANT[m.status] ?? 'bg-secondary text-secondary-foreground'
                        }
                      />
                    </div>
                    {/* texto exatamente como o cliente recebe, incluindo o link */}
                    <p className="mt-2 leading-relaxed [overflow-wrap:anywhere]">{m.body}</p>
                    {m.checkoutUrl && (
                      <a
                        className="mt-3 inline-flex max-w-full items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium hover:bg-accent"
                        href={m.checkoutUrl}
                        target="_blank"
                        rel="noreferrer"
                        title={m.checkoutUrl}
                      >
                        <ExternalLink className="size-3.5 shrink-0" /> Abrir checkout
                      </a>
                    )}
                    {m.errorMessage && (
                      <p className="mt-2 rounded bg-destructive/10 p-2 text-xs text-destructive">
                        {m.errorMessage}
                      </p>
                    )}
                    <p className="mt-2 text-xs text-muted-foreground">
                      Enviado {fmtDateTime(m.sentAt)}
                      {m.deliveredAt ? ` · entregue ${fmtDateTime(m.deliveredAt)}` : ''}
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
              <CardTitle className="text-base">E-mails com o link (segunda via)</CardTitle>
              {canManage && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => sendEmail.mutate()}
                  disabled={sendEmail.isPending}
                >
                  {sendEmail.isPending ? <Loader2 className="animate-spin" /> : <Mail />} Enviar por
                  e-mail
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {call.emailMessages?.length ? (
                call.emailMessages.map((m) => (
                  <div key={m.id} className="rounded-lg border p-3 text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-mono text-xs text-muted-foreground">{m.toEmail}</span>
                      <StatusBadge
                        label={EMAIL_STATUS_LABEL[m.status] ?? m.status}
                        className={
                          EMAIL_STATUS_VARIANT[m.status] ?? 'bg-secondary text-secondary-foreground'
                        }
                      />
                    </div>
                    <p className="mt-2">{m.subject}</p>
                    {m.errorMessage && (
                      <p className="mt-2 rounded bg-destructive/10 p-2 text-xs text-destructive">
                        {m.errorMessage}
                      </p>
                    )}
                    <p className="mt-2 text-xs text-muted-foreground">
                      {EMAIL_REASON_LABEL[m.reason] ?? m.reason} · enviado {fmtDateTime(m.sentAt)}
                      {m.deliveredAt ? ` · entregue ${fmtDateTime(m.deliveredAt)}` : ''}
                      {m.openedAt ? ` · aberto ${fmtDateTime(m.openedAt)}` : ''}
                      {m.clickedAt ? ` · link clicado ${fmtDateTime(m.clickedAt)}` : ''}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">
                  Nenhum e-mail enviado. Sai sozinho quando o SMS não é entregue ou quando a IA
                  combina e-mail na ligação; o botão envia agora para o e-mail do lead.
                </p>
              )}
            </CardContent>
          </Card>
          {call.recordingUrl && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Gravação</CardTitle>
              </CardHeader>
              <CardContent>
                <audio controls preload="none" src={call.recordingUrl} className="w-full" />
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Transcrição</CardTitle>
            </CardHeader>
            <CardContent>
              {turns?.length ? (
                <ol className="space-y-3">
                  {turns.map((t, i) => (
                    <li
                      key={i}
                      className={`flex gap-3 ${t.role === 'user' ? 'flex-row-reverse' : ''}`}
                    >
                      <div
                        className={`flex size-7 shrink-0 items-center justify-center rounded-full ${t.role === 'user' ? 'bg-secondary' : 'bg-primary/15 text-primary'}`}
                      >
                        {t.role === 'user' ? (
                          <UserIcon className="size-4" />
                        ) : (
                          <Bot className="size-4" />
                        )}
                      </div>
                      <div
                        className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${t.role === 'user' ? 'bg-secondary' : 'bg-primary/10'}`}
                      >
                        {t.content}
                      </div>
                    </li>
                  ))}
                </ol>
              ) : call.transcript ? (
                <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">
                  {call.transcript}
                </pre>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {live
                    ? 'A transcrição aparece quando a ligação terminar.'
                    : 'Sem transcrição disponível.'}
                </p>
              )}
            </CardContent>
          </Card>

          {call.provider === 'mock' && live && canManage && (
            <Card className="border-dashed">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <FlaskConical className="size-4" /> Simulador de webhook (provedor mock)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <MockWebhookSimulator providerCallId={call.providerCallId!} />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
